import { z } from 'zod'
import { CmicIdpProvider, IntegrationType } from '../enums.js'

/**
 * Vendor-specific credential schemas, kept in common-all so they can be
 * consumed from the GraphQL schema layer and the admin-dashboard typed
 * fetch result. The encrypt/decrypt machinery lives in the server-only
 * `packages/common/src/utils/integration-credential.ts`.
 */

const texturaCredentialsSchema = z.object({
  username: z.string(),
  password: z.string(),
  emailAddress: z.string().nullable(),
  onePasswordItemId: z.string().nullable(),
  onePasswordVaultId: z.string().nullable(),
})
export type TexturaCredentials = z.infer<typeof texturaCredentialsSchema>

const gcPayCredentialsSchema = z.object({
  username: z.string(),
  password: z.string(),
})
export type GcPayCredentials = z.infer<typeof gcPayCredentialsSchema>

const hh2IntegrationCredentialsSchema = z.object({
  subdomain: z.string(),
  username: z.string(),
  password: z.string(),
})
export type Hh2IntegrationCredentials = z.infer<typeof hh2IntegrationCredentialsSchema>

const agaveCredentialsSchema = z.object({
  accountToken: z.string(),
})
export type AgaveCredentials = z.infer<typeof agaveCredentialsSchema>

// Shared OAuth token credential shape for peyote integrations. Both Procore and QuickBooks Online
// Peyote store exactly these fields. `createdFromEnv` records which environment minted the tokens
// (see config's serviceEnvironmentShort), so a seeded lower environment knows to mint a fresh access
// token against the origin (via GET /credentials) instead of rotating the origin's token locally.
const oauthTokenCredentialsSchema = z.object({
  accessToken: z.string(),
  refreshToken: z.string(),
  clientId: z.string(),
  expiresAt: z.string(),
  createdFromEnv: z.enum(['local', 'staging', 'prod']).optional(),
})
export type OauthTokenCredentials = z.infer<typeof oauthTokenCredentialsSchema>

// Procore and QBO Peyote share the OAuth token shape; the distinct type names
// document intent at call sites without duplicating the schema.
export type ProcorePeyoteCredentials = OauthTokenCredentials
export type QuickbooksOnlinePeyoteCredentials = OauthTokenCredentials

// Sage Intacct Peyote authenticates against the XML Gateway with Web Services
// credentials — a per-company login (company id, user id, password), NOT OAuth.
// The sender id/password are normally Siteline's own partner credentials from
// config. Some customers pre-date the new understanding with Sage Intacct that we
// need to use our own sender id, and supply their own sender credentials instead;
// when set, these override config, otherwise the partner sender credentials are used.
const sageIntacctPeyoteCredentialsSchema = z.object({
  companyId: z.string(),
  userId: z.string(),
  userPassword: z.string(),
  senderId: z.string().optional(),
  senderPassword: z.string().optional(),
})
export type SageIntacctPeyoteCredentials = z.infer<typeof sageIntacctPeyoteCredentialsSchema>

const sage100PeyoteCredentialsSchema = z.object({
  secretKey: z.string(),
  ngrokAuthToken: z.string(),
})
export type Sage100PeyoteCredentials = z.infer<typeof sage100PeyoteCredentialsSchema>

const vistaPeyoteCredentialsSchema = z.object({
  host: z.string(),
  port: z.number(),
  database: z.string(),
  user: z.string(),
  password: z.string(),
  // Set in a second step after the user picks an HQCo from the JCCOCompany list.
  vistaCompanyId: z.number().optional(),
})
export type VistaPeyoteCredentials = z.infer<typeof vistaPeyoteCredentialsSchema>

const foundationPeyoteCredentialsSchema = z.object({
  host: z.string(),
  port: z.number(),
  database: z.string(),
  user: z.string(),
  password: z.string(),
  // Some Foundation SQL Server instances require TLS; others (e.g. Foundation Software's
  // hosted training server) do not. Defaults to false.
  encrypt: z.boolean().optional(),
})
export type FoundationPeyoteCredentials = z.infer<typeof foundationPeyoteCredentialsSchema>

const spectrumPeyoteCredentialsSchema = z.object({
  host: z.string(),
  port: z.number(),
  database: z.string(),
  user: z.string(),
  password: z.string(),
  // Set in a second step after the user picks a company.
  companyCode: z.string().optional(),
})
export type SpectrumPeyoteCredentials = z.infer<typeof spectrumPeyoteCredentialsSchema>

// Acumatica registers its OAuth client per-instance (Connected Applications screen), so unlike
// Procore/QBO there's no single global Siteline app — each customer's instance has its own client
// id/secret, captured here alongside the instance URL and tenant name.
const acumaticaPeyoteCredentialsSchema = z.object({
  instanceUrl: z.string(),
  // Optional: some instances (e.g. `https://mycompany.acumatica.com/`) mount everything at the root,
  // with no instance-name path segment to append.
  instanceName: z.string().optional(),
  clientId: z.string(),
  clientSecret: z.string(),
  // Populated after the OAuth authorize flow completes; the instance config above is saved first.
  accessToken: z.string().optional(),
  refreshToken: z.string().optional(),
  expiresAt: z.string().optional(),
  createdFromEnv: z.enum(['local', 'staging', 'prod']).optional(),
})
export type AcumaticaPeyoteCredentials = z.infer<typeof acumaticaPeyoteCredentialsSchema>

/**
 * Returns an Acumatica instance's application root URL. Acumatica mounts both its IdentityServer
 * (OAuth) and contract-based REST API under this path. When an instance name is set the root is
 * `{instanceUrl}/{instanceName}`; otherwise the instance is mounted at the URL root.
 */
export function getAcumaticaInstanceBaseUrl({
  instanceUrl,
  instanceName,
}: Pick<AcumaticaPeyoteCredentials, 'instanceUrl' | 'instanceName'>): string {
  const trimmedUrl = instanceUrl.replace(/\/$/, '')
  const trimmedName = instanceName?.replace(/^\/|\/$/g, '')
  return trimmedName ? `${trimmedUrl}/${trimmedName}` : trimmedUrl
}

// Basic Auth: the username is CMiC's `CLIENT_ID||USER_ID` combination, assembled in the
// admin setup form. Used by on-prem instances and cloud instances without OAuth.
const cmicPeyoteBasicCredentialsSchema = z.object({
  type: z.literal('basic'),
  // Server URL up to `.com`, e.g. `https://atlas-api.cmiccloud.com` (cloud) or the
  // customer's own host (on-prem). Joined with `environment` to form the API root.
  serverUrl: z.string(),
  // Environment path segment mounted under the server URL, e.g. `cmicprod`.
  environment: z.string(),
  username: z.string(),
  password: z.string(),
})
export type CmicPeyoteBasicCredentials = z.infer<typeof cmicPeyoteBasicCredentialsSchema>

// OAuth: an authorization-code flow against the instance's identity provider (CMiC doesn't run its
// own authorization server, so each provider's endpoints are hardcoded server-side — see
// `CMIC_IDP_ENDPOINTS` — and setup only collects the client id/secret). Unlike every other OAuth
// integration here, CMiC authenticates with the provider's **ID token**, not its access token; the
// access token is kept only because the refresh bookkeeping is shared.
const cmicPeyoteOauthCredentialsSchema = z.object({
  type: z.literal('oauth'),
  // Server URL up to `.com`, e.g. `https://atlas-api.cmiccloud.com`. CMiC serves OAuth from a
  // different host than Basic Auth. Joined with `environment` to form the API root.
  serverUrl: z.string(),
  // Environment path segment mounted under the server URL, e.g. `cmicprod`.
  environment: z.string(),
  idpProvider: z.enum(CmicIdpProvider),
  // Registered per customer with their identity provider, so each instance has its own client.
  clientId: z.string(),
  clientSecret: z.string(),
  // Populated once the authorize flow completes; the instance + client config is saved first.
  accessToken: z.string().optional(),
  // What actually goes in CMiC's `Authorization` header.
  idToken: z.string().optional(),
  refreshToken: z.string().optional(),
  expiresAt: z.string().optional(),
  createdFromEnv: z.enum(['local', 'staging', 'prod']).optional(),
})
export type CmicPeyoteOauthCredentials = z.infer<typeof cmicPeyoteOauthCredentialsSchema>

// The token set CMiC's OAuth mode keeps refreshed. Spelled out separately from the shared
// Procore/QBO shape above because CMiC authenticates with the identity provider's OIDC ID token
// rather than its access token, so an ID token is required rather than incidental.
const cmicOauthTokenCredentialsSchema = z.object({
  accessToken: z.string(),
  refreshToken: z.string(),
  clientId: z.string(),
  expiresAt: z.string(),
  idToken: z.string(),
  createdFromEnv: z.enum(['local', 'staging', 'prod']).optional(),
})
export type CmicOauthTokenCredentials = z.infer<typeof cmicOauthTokenCredentialsSchema>

const cmicPeyoteCredentialsSchema = z.discriminatedUnion('type', [
  cmicPeyoteBasicCredentialsSchema,
  cmicPeyoteOauthCredentialsSchema,
])
export type CmicPeyoteCredentials = z.infer<typeof cmicPeyoteCredentialsSchema>

/** Narrows CMiC credentials to the OAuth mode. */
export function cmicPeyoteUsesOauth(
  credentials: CmicPeyoteCredentials
): credentials is CmicPeyoteOauthCredentials {
  return credentials.type === 'oauth'
}

/**
 * Returns a CMiC instance's API root URL: the server URL with the environment path
 * segment appended (e.g. `https://atlas-api.cmiccloud.com/cmicprod`). Trailing/leading
 * slashes are normalized so callers can append entity paths directly.
 */
export function getCmicApiBaseUrl({
  serverUrl,
  environment,
}: {
  serverUrl: string
  environment: string
}): string {
  const trimmedUrl = serverUrl.replace(/\/$/, '')
  const trimmedEnvironment = environment.replace(/^\/|\/$/g, '')
  return `${trimmedUrl}/${trimmedEnvironment}`
}

export const integrationCredentialsSchema = z.object({
  textura: texturaCredentialsSchema.optional(),
  gcPay: gcPayCredentialsSchema.optional(),
  hh2: hh2IntegrationCredentialsSchema.optional(),
  agave: agaveCredentialsSchema.optional(),
  procorePeyote: oauthTokenCredentialsSchema.optional(),
  quickbooksOnlinePeyote: oauthTokenCredentialsSchema.optional(),
  foundationPeyote: foundationPeyoteCredentialsSchema.optional(),
  sageIntacctPeyote: sageIntacctPeyoteCredentialsSchema.optional(),
  spectrumPeyote: spectrumPeyoteCredentialsSchema.optional(),
  sage100Peyote: sage100PeyoteCredentialsSchema.optional(),
  vistaPeyote: vistaPeyoteCredentialsSchema.optional(),
  acumaticaPeyote: acumaticaPeyoteCredentialsSchema.optional(),
  cmicPeyote: cmicPeyoteCredentialsSchema.optional(),
})
export type IntegrationCredentials = z.infer<typeof integrationCredentialsSchema>

/**
 * Maps each integration type to its slot in the encrypted credentials blob, or null when the type
 * stores no credentials (file-based imports, TEST). Agave integrations share the `agave` slot,
 * hh2 integrations the `hh2` slot, and each peyote/direct integration its own.
 */
export const CREDENTIAL_SLOT = {
  [IntegrationType.ACUMATICA]: 'agave',
  [IntegrationType.ACUMATICA_PEYOTE]: 'acumaticaPeyote',
  [IntegrationType.CMIC]: 'agave',
  [IntegrationType.CMIC_PEYOTE]: 'cmicPeyote',
  [IntegrationType.COMPUTER_EASE_FILE]: null,
  [IntegrationType.FOUNDATION]: 'agave',
  [IntegrationType.FOUNDATION_FILE]: null,
  [IntegrationType.FOUNDATION_PEYOTE]: 'foundationPeyote',
  [IntegrationType.GC_PAY]: 'gcPay',
  [IntegrationType.NETSUITE_FILE]: null,
  [IntegrationType.PROCORE]: 'agave',
  [IntegrationType.PROCORE_PEYOTE]: 'procorePeyote',
  [IntegrationType.QUICKBOOKS_ENTERPRISE_FILE]: null,
  [IntegrationType.QUICKBOOKS_ONLINE]: 'agave',
  [IntegrationType.QUICKBOOKS_ONLINE_PEYOTE]: 'quickbooksOnlinePeyote',
  [IntegrationType.SAGE_100_CONTRACTOR]: 'hh2',
  [IntegrationType.SAGE_100_CONTRACTOR_AGAVE]: 'agave',
  [IntegrationType.SAGE_100_CONTRACTOR_PEYOTE]: 'sage100Peyote',
  [IntegrationType.SAGE_300_CRE]: 'hh2',
  [IntegrationType.SAGE_300_CRE_AGAVE]: 'agave',
  [IntegrationType.SAGE_INTACCT]: 'agave',
  [IntegrationType.SAGE_INTACCT_PEYOTE]: 'sageIntacctPeyote',
  [IntegrationType.SPECTRUM]: 'agave',
  [IntegrationType.SPECTRUM_PEYOTE]: 'spectrumPeyote',
  [IntegrationType.TEST]: null,
  [IntegrationType.TEXTURA]: 'textura',
  [IntegrationType.VISTA]: 'agave',
  [IntegrationType.VISTA_PEYOTE]: 'vistaPeyote',
} as const satisfies Record<IntegrationType, keyof IntegrationCredentials | null>
